import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, FileText, Package, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import monetizationLogo from "@/assets/monetization-logo-new.png";
import { MonetizationStepper } from "@/components/monetization/MonetizationStepper";

const FORMATS = [
  {
    id: "mini_guide",
    icon: FileText,
    title: "Mini Guide",
    detail: "5–15 pages",
    price: "$7–$27",
    description: "Quick win, easy to create. Perfect for a focused topic your audience needs solved fast.",
  },
  {
    id: "full_ebook",
    icon: BookOpen,
    title: "Full Ebook",
    detail: "20–50 pages",
    price: "$27–$97",
    description: "Deep value and authority builder. Comprehensive coverage of a topic your audience cares about.",
  },
  {
    id: "template_pack",
    icon: Package,
    title: "Template Pack",
    detail: "5–10 templates",
    price: "$17–$47",
    description: "Plug-and-play resources your audience can use immediately. Low effort, high perceived value.",
  },
  {
    id: "mini_course",
    icon: Sparkles,
    title: "Mini Course",
    detail: "5–10 short lessons",
    price: "$47–$197",
    description: "Premium format with the highest price ceiling. Short video or text lessons that deliver a transformation.",
  },
];

export default function MonetizationFormat() {
  const { projectId, productId } = useParams();
  const navigate = useNavigate();
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null);

  const handleContinue = () => {
    if (!selectedFormat) return;
    navigate(`/app/monetization-ai/project/${projectId}/structure/${productId}?format=${selectedFormat}`);
  };

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/app/monetization-ai/project/${projectId}/products`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl overflow-hidden">
            <img src={monetizationLogo} alt="Monetization AI" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Choose Your Product Format</h1>
            <p className="text-muted-foreground">Select the format that best fits your product</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="ml-14">
          <MonetizationStepper currentStep="building" />
        </div>

        {/* Format Cards */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          {FORMATS.map((format) => (
            <div
              key={format.id}
              onClick={() => setSelectedFormat(format.id)}
              className={`glass-card p-6 cursor-pointer transition-all ${
                selectedFormat === format.id
                  ? "border-green-500 bg-green-500/10 ring-2 ring-green-500/20"
                  : "border-border/50 hover:border-green-500/50"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
                  selectedFormat === format.id ? "bg-green-500/20" : "bg-muted"
                }`}>
                  <format.icon className={`w-7 h-7 ${
                    selectedFormat === format.id ? "text-green-400" : "text-muted-foreground"
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg font-semibold mb-1">{format.title}</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{format.detail}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">{format.price}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{format.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Continue */}
        <div className="flex justify-end">
          <Button variant="hero" size="lg" onClick={handleContinue} disabled={!selectedFormat}>
            Continue
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
